/**
 * ExtractionResult as a Zod schema — the JSON schema for the structured-output call is derived from
 * this, and `messages.ts`'s interface is checked against it below so the two cannot drift.
 */
import { z } from 'zod';
import type { ExtractionResult, VoucherResult } from './messages.js';

export const CLASSIFICATIONS = ['ticket_with_instructions', 'already_factura', 'never_facturable', 'foreign', 'platform_receipt', 'no_channel'] as const;
export const PLATFORMS = ['uber', 'uber_eats', 'didi', 'rappi', 'hoteles_com', 'amazon', 'airline', 'other'] as const;
export const CHANNELS = ['email', 'portal', 'qr', 'platform', 'none'] as const;

export const ExtractionResultSchema = z.object({
  classification: z.enum(CLASSIFICATIONS),
  platform: z.enum(PLATFORMS).optional(),
  merchant: z.string().optional(),
  merchant_rfc: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  total: z.number().optional(),
  subtotal: z.number().optional(),
  iva: z.number().optional(),
  currency: z.string().optional(),
  folio: z.string().optional(),
  ticket_codes: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
  facturacion: z
    .object({
      channel: z.enum(CHANNELS),
      email: z.string().optional(),
      url: z.string().optional(),
      qr_payload: z.string().optional(),
      footer_visible: z.boolean(),
      window_text: z.string().optional(),
    })
    .optional(),
  source_kind: z.enum(['xml', 'pdf_text', 'image']).optional(),
  foreign_vendor: z
    .object({ legal_name: z.string().optional(), address: z.string().optional(), tax_id: z.string().optional() })
    .optional(),
  payments: z.array(z.object({ method: z.string(), amount: z.number() })).optional(),
  confidence: z.object({ merchant: z.number(), date: z.number(), total: z.number(), folio: z.number() }),
});

export const VoucherResultSchema = z.object({
  is_voucher: z.boolean(),
  merchant: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  amount: z.number().optional(),
  tip: z.number().optional(),
  authorization: z.string().optional(),
  last4: z.string().optional(),
  confidence: z.object({ amount: z.number(), date: z.number() }),
});

// Compile-time check: the schema's inferred type must be assignable both ways to the interface.
type Inferred = z.infer<typeof ExtractionResultSchema>;
const _a: ExtractionResult = null as unknown as Inferred;
const _b: Inferred = null as unknown as ExtractionResult;
void _a; void _b;
type InferredVoucher = z.infer<typeof VoucherResultSchema>;
const _c: VoucherResult = null as unknown as InferredVoucher;
const _d: InferredVoucher = null as unknown as VoucherResult;
void _c; void _d;

/** extraction-prompt.md: any required field below this → needs-retake. */
export const RETAKE_THRESHOLD = 0.6;

export type RequiredField = keyof ExtractionResult['confidence'];

/**
 * Which fields must be read confidently before the receipt is accepted (decided 3 Sep 2026 after the first live run):
 *  - total and date always — they drive the export and the deadlines;
 *  - merchant for anything a recovery will be attempted on (ticket_with_instructions, platform_receipt, no_channel);
 *  - folio ONLY when a portal will ask for it: ticket_with_instructions with a portal/qr channel. Email requests and
 *    platform portals (Uber, Uber Eats… work off the account, not a printed code) do not need one, and a receipt that
 *    prints no folio must never send the employee back to the camera.
 */
export function requiredFields(classification: ExtractionResult['classification'], channel?: ExtractionResult['facturacion'] extends infer F ? (F extends { channel: infer C } ? C : never) : never): RequiredField[] {
  switch (classification) {
    case 'already_factura': case 'never_facturable': case 'foreign': return ['total', 'date'];
    case 'no_channel': case 'platform_receipt': return ['merchant', 'date', 'total'];
    case 'ticket_with_instructions': return channel === 'portal' || channel === 'qr' ? ['merchant', 'date', 'total', 'folio'] : ['merchant', 'date', 'total'];
  }
}

/** The fields that fell below the threshold; empty means the extraction is accepted. */
export function lowConfidenceFields(r: ExtractionResult): RequiredField[] {
  return requiredFields(r.classification, r.facturacion?.channel).filter((f) => (r.confidence[f] ?? 0) < RETAKE_THRESHOLD);
}

/** Static platform → facturación URL table (extraction-prompt.md, output handling). */
export const PLATFORM_PORTALS: Record<(typeof PLATFORMS)[number], string | null> = {
  uber: 'https://riders.uber.com/trips',
  uber_eats: 'https://www.ubereats.com/mx/orders',
  didi: 'https://facturacion.didiglobal.com/',
  rappi: 'https://www.rappi.com.mx/facturacion',
  hoteles_com: 'https://www.hoteles.com/facturacion',
  amazon: 'https://www.amazon.com.mx/gp/css/order-history',
  airline: null, // resolved per carrier by the router (Viva, Volaris, Aeroméxico) — not from the ticket
  other: null,
};
